import { useEffect, useMemo, useState } from 'react';
import { api, ChatPerson } from '../../api';
import { useApp } from '../../App';
import { useIsMobile } from '../../lib/media';
import { useBackEntry } from '../../nav/useBackEntry';
import { PanelHead } from '../../nav/PanelHead';
import { PersonRows } from '../ChatDialogs';
import { IconSearch } from '../../components/icons';
import type { CallKind } from './callRoom';

/*
 * Выбор людей для звонка. Одно и то же окно решает две задачи: позвать ещё
 * человека в идущий звонок и выбрать, кого звать из большого канала, — набор
 * людей и подпись кнопки приходят снаружи. На телефоне это экран стопки:
 * список на пол-экрана в попапе выбирать неудобно.
 */

/** Столько мест в звонке; то же правило на сервере (MAX_PARTICIPANTS). */
export const MAX_IN_CALL = 10;

export function InviteDialog({
  title,
  confirm,
  people,
  taken,
  limit,
  onPick,
  onClose,
  withKind,
  link,
  onlyLink,
}: {
  title: string;
  /** Подпись кнопки без счётчика: «Пригласить», «Позвонить». */
  confirm: string;
  /** Из кого выбирать. Не задано — все сотрудники чата. */
  people?: ChatPerson[];
  /** Кто уже в звонке: отмечен и недоступен. */
  taken: Set<string>;
  /** Сколько человек ещё можно позвать. */
  limit: number;
  onPick: (userIds: string[], kind?: CallKind) => void;
  onClose: () => void;
  /** Вид звонка выбирают здесь же: в подвале две кнопки вместо одной. */
  withKind?: boolean;
  /** Ссылка комнаты для гостей; пусто — звонок без комнаты. */
  link?: string;
  /** Во встречу зовут через её карточку: здесь остаётся только ссылка. */
  onlyLink?: boolean;
}) {
  const app = useApp();
  const mobile = useIsMobile();
  const [all, setAll] = useState<ChatPerson[]>(people ?? []);
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [copied, setCopied] = useState(false);

  // системная «назад» на телефоне закрывает выбор, а не весь звонок
  useBackEntry(mobile, onClose);

  useEffect(() => {
    if (people) return;
    api.chatPeople().then(setAll).catch(() => {});
  }, [people]);

  const list = useMemo(() => all.filter((p) => p.id !== app.user.id), [all, app.user.id]);
  const full = picked.size >= limit;

  const toggle = (p: ChatPerson) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(p.id)) next.delete(p.id);
      // за лимит не пускаем здесь, а не отказом сервера после нажатия
      else if (next.size < limit) next.add(p.id);
      return next;
    });
  };

  const submit = (kind?: CallKind) => {
    if (!picked.size) return;
    onPick([...picked], kind);
  };

  // Ссылку жмут в спешке посреди звонка: надпись подтверждает, что она в
  // буфере, и через две секунды возвращается — второй раз копировать можно.
  const copy = () => {
    if (!link) return;
    void navigator.clipboard.writeText(link).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const body = (
    <>
      {link && (
        <div className="row static">
          <span className="row-text">
            <span className="row-title">Ссылка для гостей</span>
            <span className="row-sub">{link}</span>
          </span>
          <button className="btn outline sm" onClick={copy}>
            {copied ? 'Скопировано' : 'Скопировать'}
          </button>
        </div>
      )}
      {!onlyLink && (
        <>
      <label className="search">
        <IconSearch />
        <input
          value={query}
          placeholder="Имя или никнейм"
          aria-label="Имя или никнейм"
          autoFocus={!mobile}
          onChange={(e) => setQuery(e.target.value)}
        />
      </label>
      <PersonRows people={list} query={query} selected={picked} locked={taken} onPick={toggle} />
      {/* обычный текст, а не подпись-заголовок: .sub — для служебных капсов */}
      {full && <p className="sub">Больше в звонок не поместится</p>}
        </>
      )}
    </>
  );

  const actions = onlyLink ? (
    <div className="dialog-foot">
      <button className="btn primary" onClick={onClose}>
        Готово
      </button>
    </div>
  ) : (
    <div className="dialog-foot">
      <button className="btn outline" onClick={onClose}>
        Отмена
      </button>
      {withKind ? (
        <>
          <button className="btn outline" disabled={!picked.size} onClick={() => submit('audio')}>
            Аудио ({picked.size})
          </button>
          <button className="btn primary" disabled={!picked.size} onClick={() => submit('video')}>
            Видео ({picked.size})
          </button>
        </>
      ) : (
        <button className="btn primary" disabled={!picked.size} onClick={() => submit()}>
          {confirm} ({picked.size})
        </button>
      )}
    </div>
  );

  if (mobile) {
    return (
      <div className="call-pick">
        <PanelHead title={title} onBack={onClose} />
        <div className="call-pick-body">{body}</div>
        {actions}
      </div>
    );
  }

  return (
    <div className="backdrop" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="dialog" role="dialog" aria-modal="true">
        <PanelHead title={title} onBack={onClose} />
        <div className="dialog-body">{body}</div>
        {actions}
      </div>
    </div>
  );
}
